/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';
import { Modal, Space, Spin } from 'antd';
import SideNav from '../../company-settings/side-nav/component';
import { AddDepartment } from '../add-modal';
import { MEMBER_STATUS } from '../../../../constants/index';
import FilterableHeader from './filterable-header';
import { axiosInstanceB2B, Toaster } from '@share/utils';
import { useSelector } from 'react-redux';
import { ILoginState } from '@share/store/slices';
import { Urls } from '@share/constants';
import { SearchTable } from '../../common';
import { InputFilterType } from '@share/common-types';
import { get } from 'lodash';
import { FormattedMessage, useIntl } from 'react-intl';
import { BlueButton } from '@share/components';
import { ExclamationCircleOutlined } from '@ant-design/icons';

interface IMapStateToProps {
  loginStore: ILoginState;
}

const CompanyDepartmentCard: React.FC = () => {
  const login = useSelector((state: IMapStateToProps) => state.loginStore);
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setEditIsOpen] = useState(false);
  const [userData, setUserData] = useState([]);
  const [dptRoles, setDptRoles] = useState();
  const [selectedUser, setSelectedUser] = useState();
  const [filter, setFilter] = useState(MEMBER_STATUS.ALL);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [filters, setFilters] = useState({
    email: { value: null },
  });
  const [sorter, setSorter] = useState(null);
  const [departments, setDepartments] = useState([]);

  const fetchUserData = async (page = 1, pageSize = 10, filters: any, sorter: any) => {
    setLoading(true);
    try {
      const sorterParam = !!sorter?.columnKey ? `orderBy=${sorter.columnKey}&sortOrder=${sorter.order === 'ascend' ? 'asc' : 'desc'}` : '';
      const filtersParam = !!filters?.email?.value ? `&email=${filters.email.value}` : '';
      const { data: result } = await axiosInstanceB2B.get(`${Urls.GetDepartments}?orderBy=departmentName&sortOrder=asc&page=${page}&results=${pageSize}${sorterParam}${filtersParam}`);
      setDepartments(result?.items);
      setUserData(result.items);
      setTotalItems(result?.totalResults);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    setLoading(true);
    try {
      await axiosInstanceB2B.post(`${Urls.SaveDepartment}`,values);
      await fetchUserData(pagination?.current, pagination?.pageSize, filters, sorter);
      if (!values?.id) {
        Toaster.success(`${intl.formatMessage({ id: 'department.added' })}`);
      } else {
        Toaster.success(`${intl.formatMessage({ id: 'department.updated' })}`);
      }
    } catch (error) {
      console.error(`${intl.formatMessage({ id: 'error.fetching.user.data' })}`, error);
      Toaster.error(`Error ${!values?.userId ? intl.formatMessage({ id: 'adding' }) : intl.formatMessage({ id: 'updating' })} ${intl.formatMessage({ id: 'error.fetching.user.data.message' })}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: any) => {
    setEditIsOpen(true);
    setSelectedUser(user);
  };

  const handleOnTableChange = (pagination: any, filters: any, sorter: any) => {
    if (pagination) {
      setPagination(pagination as any);
    }

    if (!!filters.email?.length) {
      setFilters({
        email: { 
          value: get(get(filters.email, '[0]', '')?.split('~'), '[0]')
        }
      });
    }

    if (sorter) {
      setSorter(sorter as any);
    }
  };

  useEffect(() => {
    fetchUserData(pagination?.current, pagination?.pageSize, filters, sorter);
  }, [pagination?.current, pagination?.pageSize, filters, sorter]);

  const { confirm } = Modal;
  const handleDelete = async (id: number) => {
    setLoading(true);
    try {
      const response = await axiosInstanceB2B.delete(`${Urls.DeleteDepartment}/${id}`);
      if(response.data.success === false) {
        Toaster.warning(response.data.message);
      } else if(response.data.success === true) {
        Toaster.success(response.data.message);
        await fetchUserData(pagination?.current, pagination?.pageSize, filters, sorter);
      }
    } catch (error: any) {
      Toaster.error(error.response?.data?.errors[0]?.message);
    } finally {
      setLoading(false);
    }
  };
  const showPromiseConfirm = (id: number) => {
    confirm({
      title: intl.formatMessage({ id: 'delete.items' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'delete.items.message' }),
      onOk() {
        return new Promise((resolve, reject) => {
          handleDelete(id);
          setTimeout(Math.random() > 0.5 ? resolve : reject, 1000);
        }).catch((error) => console.error(error));
      },
      onCancel() {},
    });
  };
  const columns: any[] = [
    {
      description: intl.formatMessage({ id: 'department.name' }),
      key: 'departmentName',
      type: InputFilterType.Text,
      width: 275,
      display: true,
      order: 10,
      sortable: false,
      component: (_text: any, record: any) => (
        <span>
          {record.departmentName}
        </span>
      )
    },
    {
      description: intl.formatMessage({ id: 'status' }),
      key: 'activeState',
      type: InputFilterType.IntegerNumber,
      width: 140,
      display: true,
      order: 40,
      sortable: false,
      component: (_text: any) => <>
        <span>
          {_text === 1 ? <FormattedMessage id='active' /> : <FormattedMessage id='inactive' />}
        </span>
        </>
    },
    {
      description: intl.formatMessage({ id: 'action' }),
      key: 'action',
      type: InputFilterType.Text,
      width: 140,
      display: true,
      order: 40,
      sortable: false,
      component: (_text: any, record: any) => (
        <>
        <Space size="middle">
          <a onClick={() => handleEdit(record)}>
            <i className="bi bi-pencil-square"></i>
          </a>
          <a onClick={() => showPromiseConfirm(record.departmentId)}>
            <i className="bi bi-trash"></i>
          </a>
        </Space>
        </>
      ),
    },
  ];

  return (
    <>
      {isOpen && (
        <AddDepartment
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          dptRoles={dptRoles}
          userData={{accountId: login.account?.id}}
          onSave={handleSave}
          intl={intl}
        />)}

      {isEditOpen && (
        <AddDepartment
          isOpen={isEditOpen}
          setIsOpen={setEditIsOpen}
          dptRoles={dptRoles}
          userData={selectedUser}
          onSave={handleSave}
          intl={intl}
        />)}

      <section className="pt-3">
        <div className="container">
          <div className="row">
            <SideNav />
            <div className="col-lg-8 col-xl-9">
              <div className="card shadow-sm border">
                <div className="card-header bg-transparent border-bottom">
                  <h4 className="card-header-title">
                    <i className="bi bi-diagram-3 mx-2"></i><FormattedMessage id="departments" />
                    <BlueButton
                        block
                        size="large"
                        type="primary"
                        className="btn btn-primary mb-0 float-end commonBlueBtn"
                        onClick={() => setIsOpen(true)}
                      >
                        <i className="bi bi-person-plus me-2"></i><FormattedMessage id="add.new.button" />
                      </BlueButton>
                  </h4>
                  
                </div>
                <div className="card-body p-2 p-sm-4">
                  <div className="row">
                    {(loading || departments?.length > 0 || !!filters.email.value) ? (
                      <Spin spinning={loading}>

                        <SearchTable
                          key={'active'}
                          title={''}
                          loading={loading}
                          allColumns={[...columns]}
                          dataSource={departments || []}
                          pagination={pagination}
                          filters={filters}
                          totalOrderCount={totalItems || 0}
                          rowKey={(row: any) => {
                            return row ? `${row?.userId}` : `${row?.userId}`;
                          } }
                  
                          hideRefresh={true} 
                          isFilterApplied={false}
                          onFiltersChange={() => {}}
                          onTableChange={handleOnTableChange}
                          onRefresh={() => {}}
                          onRowClick={() => {}}
                          onResetFilters={() => {}}
                          onApplyFilters={() => {}}
                          onExport={() => {}}
                        ></SearchTable>
                      </Spin>
                    ) : (
                      <div className="text-center"><FormattedMessage id="no.data" /></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CompanyDepartmentCard;
